import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { AttentionMessage, InfoMessage } from '@eika/attention-message';
import { ButtonTypes, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { IconTypes } from '@eika/icon';

import { TransactionSubmitWrapper } from '../../components/TransactionForm';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import AvtalegiroUpdateInfo from '../../components/transaction/AvtaleGiro/AvtalegiroUpdateInfo';
import { AvtaleGiroActionType } from '../../components/transaction/AvtaleGiro/form/actions';
import createAvtalegiroUpdateRequest from '../../components/transaction/AvtaleGiro/form/createAvtalegiroUpdateRequest';
import useFormContext from '../../components/transaction/AvtaleGiro/form/useFormContext';
import { hasSigningError } from '../../components/transaction/Payment/PaymentForm';
import { SelectedReceiver } from '../../components/transaction/SelectedReceiver';
import TransactionAmount from '../../components/transaction/TransactionDetails/TransactionAmount';
import UpdateRetry from '../../components/transaction/UpdateRetry';
import { useAccountNumber, useAccounts } from '../../fetchHooks/useAccount';
import { usePaymentItemDetails, useRestartStoppedPayment } from '../../fetchHooks/usePaymentItems';
import type { AvtalegiroAgreementDetails, PaymentAgreementId, PaymentItem } from '../../generated-types';
import { usePaymentAccountContext } from '../../hooks/useAccountState';
import useTranslation from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import AvtaleGiroEditableDetails from '../AvtaleGiro/AvtaleGiroEditableDetails';
import PaymentListItemContent from './PaymentListItemContent';
import StoppedAvtalegiroFormFooter from './StoppedAvtalegiroFormFooter';
import { formatPaymentItem } from './formatPaymentItems';
import getDetailedStoppedDescriptionAndLink from './getDetailedStoppedDescription';

type Props = {
    paymentItem: PaymentItem;
    avtalegiroId: PaymentAgreementId;
    receiver: SelectedReceiver | undefined;
    avtalegiroDetails: AvtalegiroAgreementDetails;
    onClose: () => void;
    setIsSuccess: (value: boolean) => void;
};

const StoppedAvtalegiroFormContent: React.FC<Props> = ({
    paymentItem,
    receiver,
    avtalegiroDetails,
    onClose,
    avtalegiroId,
    setIsSuccess,
}) => {
    const translated = useTranslation();
    const { data: paymentDetails, isLoading: isLoadingPaymentDetails } = usePaymentItemDetails(
        paymentItem.paymentItemId,
        { enabled: paymentItem.permissions?.canView },
    );
    const { data: fromAccount } = useAccountNumber(
        paymentItem.from?.accountNumber ?? paymentDetails?.from.accountNumber,
    );

    const { targetFromAccount } = usePaymentAccountContext();

    const { startSigning, signingStatus, signingError, showPaymentRetry, handleCancelSigningFlow } = useSigningContext(
        createAvtalegiroUpdateRequest(),
    );

    const restartPayment = useRestartStoppedPayment();

    const [openAmountLimitsModal, setOpenAmountLimitsModal] = useState(false);

    const { checkFormValidity, state, validateIsLoading, handleChange } = useFormContext();

    useEffect(() => {
        if (receiver) handleChange({ type: AvtaleGiroActionType.TO_ACCOUNT_SELECTED, payload: receiver });
    }, [receiver, handleChange]);

    const detailedStoppedDescription = useMemo((): { text: string; link: ReactNode | undefined } | null => {
        const links = { paymentLimitsLink: undefined, accountLink: undefined, avtalegiroLink: undefined };
        return getDetailedStoppedDescriptionAndLink(translated, links, fromAccount, paymentItem);
    }, [paymentItem, fromAccount, translated]);

    const { data: accounts = [], ...accountState } = useAccounts();

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) startSigning({ ...state, id: avtalegiroId });
        },
        [state, checkFormValidity, startSigning, avtalegiroId],
    );

    const restartPaymentItem = useCallback(() => {
        restartPayment.mutateAsync(paymentItem.paymentItemId).then(() => setIsSuccess(true));
    }, [paymentItem, restartPayment, setIsSuccess]);

    if (restartPayment.error) {
        return (
            <ErrorCardWrapper exception={restartPayment.error}>
                <div className="stopped-payment__details-error-wrapper">
                    <LinkButton
                        onClick={() => {
                            restartPayment.reset();
                            onClose();
                        }}
                    >
                        {translated.close}
                    </LinkButton>
                    <ButtonWithSpinner
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                        showSpinner={restartPayment.isPending}
                        onClick={restartPaymentItem}
                    >
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                </div>
            </ErrorCardWrapper>
        );
    }

    if (showPaymentRetry) {
        return <UpdateRetry onClose={onClose} avtalegiro />;
    }

    if (signingError || hasSigningError(signingStatus)) {
        return (
            <AvtalegiroUpdateInfo
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                fromAccountId={targetFromAccount?.accountId}
                onClose={onClose}
            />
        );
    }

    if (signingStatus === 'COMPLETED') {
        return (
            <section className="stopped-avtalegiro-form__success-card">
                <div className="stopped-avtalegiro-form__success-card--intro">
                    <CheckmarkAnimation key={1} className="stopped-avtalegiro-form__success-card--animation" />
                    <h2 className="stopped-avtalegiro-form__success-card--title">
                        {translated.avtaleGiroAgreement.avtaleGiroUpdated}
                    </h2>
                </div>
                <div className="row-button__container">
                    <PaymentListItemContent payment={formatPaymentItem(translated)(paymentItem, accounts)} hideLabel />
                </div>
                <InfoMessage
                    text={translated.avtaleGiroAgreement.avtaleGiroWasStoppedPayment}
                    icon={IconTypes.UTROPSTEGN_24}
                />
                <div className="avtalegiro__footer in-success-card">
                    <ButtonWithSpinner
                        showSpinner={restartPayment.isPending}
                        buttonType="positive"
                        onClick={restartPaymentItem}
                    >
                        {translated.avtaleGiroAgreement.avtaleGiroStoppedPaymentSendAgain}
                    </ButtonWithSpinner>
                    <LinkButton onClick={onClose}>{translated.close}</LinkButton>
                </div>
            </section>
        );
    }

    return (
        <SigningContextFlow onClosePayment={onClose}>
            <form className="stopped-avtalegiro-form" onSubmit={onSubmit}>
                <TransactionAmount
                    value={paymentDetails?.amount ?? paymentItem.amount}
                    typeLabel={
                        paymentItem.paymentItemType !== 'UNKNOWN' && paymentItem.paymentItemType
                            ? translated.futurePaymentType[paymentItem.paymentItemType]
                            : undefined
                    }
                    eFakturaId={paymentDetails?.eFakturaId}
                    eFakturaLoading={paymentItem.paymentItemType === 'EFAKTURA' ? isLoadingPaymentDetails : false}
                    additionalInfo={
                        paymentItem.paymentItemType === 'FOREIGN_PAYMENT'
                            ? translated.futurePaymentDetails.calculatedAmount
                            : undefined
                    }
                    eFakturaLinkClassName="stopped-payment__details-eFaktura-link"
                />
                {paymentItem.subStatus && detailedStoppedDescription?.text && (
                    <AttentionMessage
                        type="attention"
                        className="stopped-payment__details-error-message"
                        icon={IconTypes.UTROPSTEGN_24}
                        clickableItem={detailedStoppedDescription?.link}
                        text={detailedStoppedDescription?.text}
                    />
                )}
                <AvtaleGiroEditableDetails
                    className="select-list__container stopped-avtalegiro-form__select-list"
                    accounts={accounts}
                    accountState={accountState}
                    receiver={receiver}
                    openAmountLimitsModal={[openAmountLimitsModal, setOpenAmountLimitsModal]}
                />

                <TransactionSubmitWrapper className="avtalegiro__footer">
                    <StoppedAvtalegiroFormFooter
                        avtalegiroDetails={avtalegiroDetails}
                        restartPayment={restartPaymentItem}
                        triggerAmountLimitModal={() => setOpenAmountLimitsModal(true)}
                        isLoading={restartPayment.isPending || validateIsLoading}
                        paymentItemSubType={paymentItem.subStatus}
                    />
                </TransactionSubmitWrapper>
            </form>
        </SigningContextFlow>
    );
};

export default StoppedAvtalegiroFormContent;
